import React from "react";
import {
  Modal,
  Table,
  Text,
  NumberFormatter,
  Card,
  Grid,
  Divider,
  Badge,
  Stack,
  Title,
} from "@mantine/core";
import { AuthType } from "../../../../api/apiAuth";

interface ItemPrice {
  itemCode: string;
  itemName: string;
  retailPrice: number;
  maxDiscountPrice: number;
  discountPrice1: number;
  discountPrice2: number;
  discountPrice3: number;
  note: string | null;
  priceListNote?: string | null;
  // Thêm các field mới cho wholesale
  importPrice?: number;
  grossProfit?: number;
  estimatedImportPrice?: number;
  estimatedGrossProfit?: number;
}

interface DiscountData {
  customerCode: string;
  numberOfPurchases: number;
  currentSales: number;
  customerGroup: number;
  itemPrices: ItemPrice[];
}

interface ListAddItem {
  itemCode?: string;
  ItemCode?: string; // Thêm cả viết hoa
  item_code?: string; // Thêm cả underscore
  discoundAdded?: number;
  discountAdded?: number; // Thêm cả spelling đúng
  priceOnPriceList?: number; // Giá bán thực tế
  [key: string]: any; // Cho phép các trường khác
}

interface DiscountSuggestionModalProps {
  opened: boolean;
  onClose: () => void;
  discountData: DiscountData | null;
  loading?: boolean;
  userAuth: any | null;
  onReceiveMaxDiscounts?: (maxDiscounts: {
    [itemCode: string]: number;
  }) => void;
  isRetail?: boolean;
  listAdd?: ListAddItem[];
  fromCompletedList?: boolean;
}

const DiscountSuggestionModal: React.FC<DiscountSuggestionModalProps> = ({
  opened,
  onClose,
  discountData,
  loading = false,
  userAuth,
  onReceiveMaxDiscounts,
  isRetail,
  listAdd = [],
  fromCompletedList = false,
}) => {


  const canViewWholesaleColumns = () => {
    // Nếu có fromCompletedList thì hiển thị tất cả các cột
    if (fromCompletedList) return true;
    
    if (!userAuth) return false;

    // Chỉ cho phép quyền = 2 (FULL_PERMISSION) mới xem được các cột wholesale
    return userAuth.typeAtuh === AuthType.FULL_PERMISSION;
  };

  // Lấy giá trị discount thực tế từ ListAdd (unused helper)
  const _getActualDiscount = (itemCode: string): number => {
    if (!listAdd || listAdd.length === 0) {
      return 0;
    }

    const item = listAdd.find((item) => item.itemCode === itemCode);
    return item?.discountAdded || 0;
  };

  // Lấy giá bán thực tế từ ListAdd
  const getActualPrice = (itemCode: string): number => {
    if (!listAdd || listAdd.length === 0) {
      return 0;
    }

    const item = listAdd.find((item) => item.itemCode === itemCode);
    return item?.priceFinal || 0;
  };

  // Hàm lấy giá theo nhóm khách hàng (unused helper)
  const _getPriceByCustomerGroup = (item: ItemPrice): number => {
    if (!discountData) return 0;
    
    switch (discountData.customerGroup) {
      case 0:
        return item.retailPrice; // Giá bán lẻ
      case 1:
        return item.discountPrice1; // Bán buôn 1
      case 2:
        return item.discountPrice2; // Bán buôn 2
      case 3:
        return item.discountPrice3; // Bán buôn 3
      default:
        return item.retailPrice;
    }
  };

  // Tính mức chênh lệch = Giá bán lẻ - Giá bán thực tế
  const getPriceDifference = (item: ItemPrice): number => {
    const actualPrice = getActualPrice(item.itemCode);
    return item.retailPrice - actualPrice;
  };

  // Gửi dữ liệu max discount lên component cha khi có dữ liệu
  React.useEffect(() => {
    if (discountData?.itemPrices && onReceiveMaxDiscounts) {
      const maxDiscounts: { [itemCode: string]: number } = {};
      discountData.itemPrices.forEach((item) => {
        maxDiscounts[item.itemCode] = item.maxDiscountPrice;
      });
      onReceiveMaxDiscounts(maxDiscounts);
    }
  }, [discountData, onReceiveMaxDiscounts]);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={4} c="white">
          Gợi ý chiết khấu cho khách hàng
        </Title>
      }
      size="90%"
      centered
    >
      {loading ? (
        <Text ta="center" p="xl">
          Đang tải dữ liệu...
        </Text>
      ) : discountData ? (
        <Stack gap="md">
          {/* Thông tin khách hàng */}
          <Card withBorder shadow="sm" p="md">
            <Title order={5} mb="sm" c="teal">
              Thông tin khách hàng
            </Title>
            <Grid>
              <Grid.Col span={3}>
                <Text size="sm" fw={500}>
                  Mã khách hàng:
                </Text>
                <Text size="sm" c="blue" fw={600}>
                  {discountData.customerCode}
                </Text>
              </Grid.Col>
              <Grid.Col span={3}>
                <Text size="sm" fw={500}>
                  Số lần mua hàng:
                </Text>
                <Text size="sm" fw={600}>
                  {discountData.numberOfPurchases} lần
                </Text>
              </Grid.Col>
                  <Grid.Col span={3}>
                <Text size="sm" fw={500}>
                  Doanh số hiện tại:
                </Text>
                <Text size="sm" fw={600} c="green">
                  <NumberFormatter
                    value={discountData.currentSales}
                    thousandSeparator="."
                    decimalSeparator=","
                    suffix=" VNĐ"
                  />
                </Text>
              </Grid.Col>
              <Grid.Col span={3}>
                <Text size="sm" fw={500}>
                  Nhóm khách hàng:
                </Text>
                <Badge
                  variant="light"
                  color={discountData.customerGroup === 0 ? "gray" : "blue"}
                >
                  Nhóm {discountData.customerGroup}
                </Badge>
              </Grid.Col>
            </Grid>
          </Card>

          <Divider />

          {/* Bảng sản phẩm và gợi ý chiết khấu */}
          <Card withBorder shadow="sm" p="md">
            <Title order={5} mb="sm" c="teal">
              Gợi ý chiết khấu theo sản phẩm
            </Title>
            <Table.ScrollContainer minWidth={1200}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>STT</Table.Th>
                    <Table.Th>Mã sản phẩm</Table.Th>
                    <Table.Th>Tên sản phẩm</Table.Th>
                    <Table.Th>Giá bán lẻ</Table.Th>
                    {/* <Table.Th>Mức bán buôn tối đa</Table.Th> */}
                    <Table.Th>
                      {isRetail ? "Mức giảm 1" : "Bán buôn 1"}
                    </Table.Th>
                    <Table.Th>
                      {isRetail ? "Mức giảm 2" : "Bán buôn 2"}
                    </Table.Th>
                    <Table.Th
                      style={{
                        backgroundColor:
                          discountData.customerGroup === 3
                            ? "#e3f2fd"
                            : "transparent",
                        color:
                          discountData.customerGroup === 3
                            ? "#1565c0"
                            : "inherit",
                        fontWeight:
                          discountData.customerGroup === 3 ? "bold" : "normal",
                      }}
                    >
                      {isRetail ? "Mức giảm 3" : "Bán buôn 3"}
                      {discountData.customerGroup === 3 && " ⭐"}
                    </Table.Th>
                    <Table.Th
                      style={{ backgroundColor: "#e8f5e8", color: "#2e7d32" }}
                    >
                      Giá bán thực tế
                    </Table.Th>
                    <Table.Th
                      style={{ backgroundColor: "#fff3cd", color: "#856404" }}
                    >
                      Mức chênh lệch
                    </Table.Th>
                    {/* Các cột wholesale chỉ hiển thị khi user có quyền */}
                    {canViewWholesaleColumns() && (
                      <>
                        <Table.Th>Giá nhập</Table.Th>
                        <Table.Th>LNG</Table.Th>
                        <Table.Th>% LNG/DT</Table.Th>
                        <Table.Th>Giá nhập tạm tính</Table.Th>
                        <Table.Th>LNG tạm tính</Table.Th>
                        <Table.Th>% LNG/DT tạm tính</Table.Th>
                      </>
                    )}
                    <Table.Th>Ghi chú</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {discountData.itemPrices.map((item, index) => (
                    <Table.Tr key={item.itemCode}>
                      <Table.Td>{index + 1}</Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500} c="blue">
                          {item.itemCode}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} maw={250}>
                          {item.itemName}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          <NumberFormatter
                            value={item.retailPrice}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      {/* <Table.Td>
                        <Text
                          size="sm"
                          fw={600}
                          c={item.maxDiscountPrice > 0 ? "red" : "gray"}
                        >
                          <NumberFormatter
                            value={item.maxDiscountPrice}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td> */}
                      <Table.Td>
                        <Text
                          size="sm"
                          fw={500}
                          c={item.discountPrice1 > 0 ? "black" : "gray"}
                        >
                          <NumberFormatter
                            value={item.discountPrice1}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          fw={500}
                          c={item.discountPrice2 > 0 ? "black" : "gray"}
                        >
                          <NumberFormatter
                            value={item.discountPrice2}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td
                        style={{
                          backgroundColor:
                            discountData.customerGroup === 3
                              ? "#e3f2fd"
                              : "transparent",
                          fontWeight:
                            discountData.customerGroup === 3
                              ? "bold"
                              : "normal",
                        }}
                      >
                        <Text
                          size="sm"
                          fw={discountData.customerGroup === 3 ? 700 : 500}
                          c={
                            discountData.customerGroup === 3
                              ? item.discountPrice3 > 0
                                ? "#1565c0"
                                : "gray"
                              : item.discountPrice3 > 0
                              ? "black"
                              : "gray"
                          }
                        >
                          <NumberFormatter
                            value={item.discountPrice3}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                          {discountData.customerGroup === 3 &&
                            item.discountPrice3 > 0 &&
                            " ⭐"}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ backgroundColor: "#e8f5e8" }}>
                        <Text size="sm" fw={600} c="#2e7d32">
                          <NumberFormatter
                            value={getActualPrice(item.itemCode)}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ backgroundColor: "#fff3cd" }}>
                        <Text
                          size="sm"
                          fw={600}
                          c={
                            getPriceDifference(item) < 0
                              ? "#d73527"
                              : "#28a745"
                          }
                        >
                          <NumberFormatter
                            value={getPriceDifference(item)}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                          {getPriceDifference(item) < 0 && " ⚠️"}
                        </Text>
                      </Table.Td>
                      {/* Các cột wholesale chỉ hiển thị khi user có quyền */}
                      {canViewWholesaleColumns() && (
                        <>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                item.importPrice && item.importPrice > 0
                                  ? "blue"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={item.importPrice || 0}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                (getActualPrice(item.itemCode) - (item.importPrice || 0)) > 0
                                  ? "green"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={getActualPrice(item.itemCode) - (item.importPrice || 0)}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                getActualPrice(item.itemCode) > 0 && 
                                ((getActualPrice(item.itemCode) - (item.importPrice || 0)) / getActualPrice(item.itemCode) * 100) > 0
                                  ? "green"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={
                                  getActualPrice(item.itemCode) > 0
                                    ? ((getActualPrice(item.itemCode) - (item.importPrice || 0)) / getActualPrice(item.itemCode) * 100)
                                    : 0
                                }
                                thousandSeparator="."
                                decimalSeparator=","
                                suffix="%"
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                item.estimatedImportPrice &&
                                item.estimatedImportPrice > 0
                                  ? "blue"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={item.estimatedImportPrice || 0}
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                (item.estimatedImportPrice || 0) === 0
                                  ? "gray"
                                  : (getActualPrice(item.itemCode) - (item.estimatedImportPrice || 0)) > 0
                                  ? "green"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={
                                  (item.estimatedImportPrice || 0) === 0
                                    ? 0
                                    : getActualPrice(item.itemCode) - (item.estimatedImportPrice || 0)
                                }
                                thousandSeparator="."
                                decimalSeparator=","
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              fw={500}
                              c={
                                (item.estimatedImportPrice || 0) === 0
                                  ? "gray"
                                  : getActualPrice(item.itemCode) > 0 && 
                                    ((getActualPrice(item.itemCode) - (item.estimatedImportPrice || 0)) / getActualPrice(item.itemCode) * 100) > 0
                                  ? "green"
                                  : "gray"
                              }
                            >
                              <NumberFormatter
                                value={
                                  (item.estimatedImportPrice || 0) === 0
                                    ? 0
                                    : getActualPrice(item.itemCode) > 0
                                    ? ((getActualPrice(item.itemCode) - (item.estimatedImportPrice || 0)) / getActualPrice(item.itemCode) * 100)
                                    : 0
                                }
                                thousandSeparator="."
                                decimalSeparator=","
                                suffix="%"
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </Text>
                          </Table.Td>
                        </>
                      )}
                      <Table.Td>
                        <Text size="sm" color={item.priceListNote ? "blue" : "gray"} lineClamp={2} maw={250}>
                          {item.priceListNote || "-"}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}

                  {/* Hàng tổng */}
                  {discountData.itemPrices.length > 0 && (
                    <Table.Tr
                      style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                    >
                      <Table.Td colSpan={4}>
                        <Text size="sm" fw={700} ta="right">
                          Tổng chênh lệch:
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={700} c="black">
                          <NumberFormatter
                            value={discountData.itemPrices.reduce(
                              (sum, item) => sum + (item.discountPrice1 || 0),
                              0
                            )}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={700} c="black">
                          <NumberFormatter
                            value={discountData.itemPrices.reduce(
                              (sum, item) => sum + (item.discountPrice2 || 0),
                              0
                            )}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td
                        style={{
                          backgroundColor:
                            discountData.customerGroup === 3
                              ? "#e3f2fd"
                              : "transparent",
                          fontWeight:
                            discountData.customerGroup === 3
                              ? "bold"
                              : "normal",
                        }}
                      >
                        <Text
                          size="sm"
                          fw={discountData.customerGroup === 3 ? 700 : 700}
                          c={
                            discountData.customerGroup === 3
                              ? "#1565c0"
                              : "black"
                          }
                        >
                          <NumberFormatter
                            value={discountData.itemPrices.reduce(
                              (sum, item) => sum + (item.discountPrice3 || 0),
                              0
                            )}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                          {discountData.customerGroup === 3 && " ⭐"}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ backgroundColor: "#e8f5e8" }}>
                        <Text size="sm" fw={700} c="#2e7d32">
                          <NumberFormatter
                            value={discountData.itemPrices.reduce(
                              (sum, item) => sum + getActualPrice(item.itemCode),
                              0
                            )}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ backgroundColor: "#fff3cd" }}>
                        <Text
                          size="sm"
                          fw={700}
                          c={
                            discountData.itemPrices.reduce(
                              (sum, item) => sum + getPriceDifference(item),
                              0
                            ) < 0
                              ? "#d73527"
                              : "#28a745"
                          }
                        >
                          <NumberFormatter
                            value={discountData.itemPrices.reduce(
                              (sum, item) => sum + getPriceDifference(item),
                              0
                            )}
                            thousandSeparator="."
                            decimalSeparator=","
                          />
                          {discountData.itemPrices.reduce(
                            (sum, item) => sum + getPriceDifference(item),
                            0
                          ) < 0 && " ⚠️"}
                        </Text>
                      </Table.Td>
                      {/* Các cột wholesale - để trống trong hàng tổng */}
                      {canViewWholesaleColumns() && (
                        <>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                        </>
                      )}
                      <Table.Td></Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {discountData.itemPrices.length === 0 && (
              <Text ta="center" c="gray" py="xl">
                Không có dữ liệu gợi ý chiết khấu
              </Text>
            )}
          </Card>

          {/* Lưu ý */}
          <Card withBorder p="sm" bg="blue.0">
            <Text size="sm" c="blue.7">
              <strong>Lưu ý:</strong> Các mức giá trên chỉ mang tính chất tham
              khảo. Vui lòng xem xét kỹ trước khi áp dụng chiết khấu.
            </Text>
            <Text size="sm" c="green.7" mt="xs">
              <strong>Giá bán thực tế:</strong> Hiển thị giá bán thực tế đang được áp dụng cho sản phẩm trong đơn hàng hiện tại.
            </Text>
            <Text size="sm" c="orange.7" mt="xs">
              <strong>Mức chênh lệch:</strong> Hiển thị chênh lệch giữa giá khách hàng được hưởng (theo nhóm) và giá bán thực tế. 
              Nhóm 0: so với Giá bán lẻ, Nhóm 1-3: so với Bán buôn tương ứng. 
              Màu đỏ ⚠️ nghĩa là bán thấp hơn giá được hưởng (âm = giảm thêm, dương = bán cao hơn giá hưởng).
            </Text>
          </Card>
        </Stack>
      ) : (
        <Text ta="center" c="red" p="xl">
          Không có dữ liệu gợi ý chiết khấu
        </Text>
      )}
    </Modal>
  );
};

export default DiscountSuggestionModal;
